import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, adminSession } from "@/lib/api";
import { toast } from "sonner";
import {
    Shield, LogOut, Trash2, Trophy, Key, Activity, ArrowLeft, X, FileJson, Inbox,
    ShieldCheck, CreditCard, Landmark, Palette, ChevronDown, RefreshCw, Smartphone, Star, AlertTriangle,
    Crown, Sparkles,
} from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { LoginScreen, InviteModal, ChangePasswordModal } from "@/components/SiteAdminParts";
import SiteAdminTemplateAvailability from "@/components/SiteAdminTemplateAvailability";
import CustomFeaturesCard from "@/components/site-admin/CustomFeaturesCard";
import MobileAccessTab from "@/components/site-admin/MobileAccessTab";
import ProPlusSubscriptionsTab from "@/components/site-admin/ProPlusSubscriptionsTab";
import ToolsAndAuditTab from "@/components/site-admin/ToolsAndAuditTab";
import CustomSubscriptionsTab from "@/components/site-admin/CustomSubscriptionsTab";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import OrphanRecoveryTab from "@/components/site-admin/OrphanRecoveryTab";
import TemplateScheduleInspectorCard from "@/components/site-admin/TemplateScheduleInspectorCard";
import SiteAdminLeagueRequests from "@/components/SiteAdminLeagueRequests";
import SiteAdminCoAdminPermissions from "@/components/SiteAdminCoAdminPermissions";
import SiteAdminSubscriptions from "@/components/SiteAdminSubscriptions";
import TenantBrandingTab from "@/components/site-admin/TenantBrandingTab";
import PlatformTenantsTab from "@/components/site-admin/PlatformTenantsTab";
import ReleaseNotesTab from "@/components/site-admin/ReleaseNotesTab";
import DocsTab from "@/components/site-admin/DocsTab";
import ComplianceTab from "@/components/site-admin/ComplianceTab";
import PlatformAdminsPlaceholderTab from "@/components/site-admin/PlatformAdminsPlaceholderTab";
import PlatformPasscodeManager from "@/components/site-admin/PlatformPasscodeManager";
import ReleaseOpsTab from "@/components/site-admin/ReleaseOpsTab";
import AccountTab from "@/components/site-admin/AccountTab";
import OverviewTab from "@/components/site-admin/OverviewTab";
import AdminsTab from "@/components/site-admin/AdminsTab";
import LeaguesTab from "@/components/site-admin/leagues/LeaguesTab";
import LLMMonitorTab from "@/components/site-admin/LLMMonitorTab";
import AdminOpsTab from "@/components/site-admin/AdminOpsTab";

// iter84.70 — Refactored: the Rivalries tab (league list, expansions,
// bulk actions, leagues-scoped modals) lives in
// components/site-admin/leagues/LeaguesTab.jsx; the passcode gate modal
// lives in components/site-admin/PlatformPasscodeModal.jsx.
export default function SiteAdmin() {
    const navigate = useNavigate();
    const [admin, setAdmin] = useState(null);
    const [tab, setTab] = useState("overview"); // overview | requests | leagues | tournaments | subscriptions | permissions | branding | admins | compliance | account (+ release_ops for platform_developer)
    // iter79 — platform_developer role: force default tab to release_ops once admin loads.
    useEffect(() => {
        if (admin?.role === "platform_developer" && tab === "overview") {
            setTab("release_ops");
        }
    }, [admin?.role, tab]);
    // iter77 gate + iter76 scope switcher were removed in iter80 (platform
    // identities live on /platform). `scopeView` persists only as a render
    // guard for platform-scope-only tab bodies.
    const [scopeView] = useState(() => {
        try { return localStorage.getItem("tcr_site_admin_scope_v1") || "site"; }
        catch { return "site"; }
    });

    // iter45f — Set browser tab title for site admin so it's distinguishable
    // from regular pages when admin has multiple tabs open.
    useEffect(() => {
        const prev = document.title;
        document.title = "Tiny Crew Rivalries — Site Admin";
        return () => { document.title = prev; };
    }, []);
    const [stats, setStats] = useState(null);
    const [leagues, setLeagues] = useState([]);
    const [siteAdmins, setSiteAdmins] = useState([]);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [changePassOpen, setChangePassOpen] = useState(false);
    const [selectedAdminIds, setSelectedAdminIds] = useState(() => new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);
    // iter86.72 — Refresh-in-place: bumping viewKey remounts the active tab
    // body (each tab refetches on mount), so no full-page reload is needed.
    const [viewKey, setViewKey] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const loadAll = useCallback(async () => {
        try {
            const [s, lg] = await Promise.all([api.siteStats(), api.siteListLeagues()]);
            setStats(s); setLeagues(lg);
        } catch (err) {
            if (err.response?.status === 401) {
                adminSession.clear(); setAdmin(null);
            } else {
                toast.error("Failed to load");
            }
        }
    }, []);

    useEffect(() => {
        const s = adminSession.load();
        if (s) {
            // Validate token still works
            api.authMe().then(a => {
                // iter80 — Identity split: platform_developer has no tenant
                // access at all → redirect them to /platform. platform_admin
                // is a SUPERSET of tenant_admin and may freely operate at /admin.
                if (a?.role === "platform_developer") {
                    navigate("/platform", { replace: true });
                    return;
                }
                setAdmin(a);
            }).catch(() => { adminSession.clear(); setAdmin(null); });
        }
        // Listen for global 401 broadcasts from the axios interceptor
        const onUnauth = () => setAdmin(null);
        window.addEventListener("admin-unauthorized", onUnauth);
        return () => window.removeEventListener("admin-unauthorized", onUnauth);
    }, [navigate]);

    // Hide /admin from search engines — inject noindex,nofollow meta on mount,
    // remove on unmount so other routes stay normally indexable.
    useEffect(() => {
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex, nofollow, noarchive";
        meta.setAttribute("data-testid", "admin-noindex-meta");
        document.head.appendChild(meta);
        return () => { document.head.removeChild(meta); };
    }, []);

    useEffect(() => {
        // iter79 — platform_developer doesn't have access to /site-admin/stats|leagues
        // (tenant_admin/platform_admin endpoints). Skip the load to avoid
        // a noisy 403 toast on every render.
        if (admin && admin.role !== "platform_developer") loadAll();
    }, [admin, loadAll]);

    useEffect(() => {
        if (admin && tab === "admins") {
            api.authListSiteAdmins()
                .then(setSiteAdmins)
                .catch(() => { /* non-fatal — render empty list */ });
        }
    }, [admin, tab]);

    const handleLogout = async () => {
        try { await api.authLogout(); } catch { /* ignore */ }
        adminSession.clear();
        setAdmin(null);
        navigate("/admin");
    };

    // Site admins eligible for deletion (excludes self + the lone super-admin)
    const adminSelectable = useMemo(() => {
        const supers = siteAdmins.filter(a => a.is_super);
        const onlyOneSuper = supers.length === 1;
        return siteAdmins.map(sa => {
            let reason = null;
            if (sa.id === admin?.id) reason = "self";
            else if (sa.is_super && onlyOneSuper) reason = "last_super";
            return { ...sa, _disabledReason: reason };
        });
    }, [siteAdmins, admin?.id]);

    const eligibleAdminIds = useMemo(
        () => adminSelectable.filter(a => !a._disabledReason).map(a => a.id),
        [adminSelectable]
    );

    const toggleAdminSelected = (id) => {
        setSelectedAdminIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAllAdmins = () => {
        setSelectedAdminIds(prev => {
            if (prev.size === eligibleAdminIds.length && eligibleAdminIds.length > 0) return new Set();
            return new Set(eligibleAdminIds);
        });
    };
    const clearSelectedAdmins = () => setSelectedAdminIds(new Set());

    const refreshView = useCallback(async () => {
        setRefreshing(true);
        setViewKey((k) => k + 1);
        try {
            const jobs = [];
            if (admin && admin.role !== "platform_developer") jobs.push(loadAll());
            if (admin && tab === "admins") jobs.push(api.authListSiteAdmins().then(setSiteAdmins));
            await Promise.all(jobs);
        } catch { /* tab components surface their own errors */ }
        setTimeout(() => setRefreshing(false), 400);
    }, [admin, tab, loadAll]);

    const bulkDeleteAdmins = async () => {
        const ids = Array.from(selectedAdminIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Permanently remove ${ids.length} site admin(s)? They will lose all access immediately.`)) return;
        setBulkDeleting(true);
        try {
            const res = await api.authBulkDeleteSiteAdmins(ids);
            if (res.deleted_count > 0) toast.success(`Removed ${res.deleted_count} site admin(s)`);
            if (res.skipped && res.skipped.length > 0) {
                const reasons = res.skipped.map(s => s.email || s.id).join(", ");
                toast.warning(`Skipped ${res.skipped.length}: ${reasons}`);
            }
            clearSelectedAdmins();
            const list = await api.authListSiteAdmins();
            setSiteAdmins(list);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk delete failed");
        }
        setBulkDeleting(false);
    };

    if (!admin) return <LoginScreen onLoggedIn={(a) => {
        // iter80 — Identity split: platform_developer has no tenant access,
        // route them to /platform. platform_admin (superset role) can stay
        // here on /admin to operate as tenant admin.
        if (a?.role === "platform_developer") {
            adminSession.save(null, a);
            navigate("/platform", { replace: true });
            return;
        }
        adminSession.save(null, a);
        setAdmin(a);
    }} />;

    return (
        <div className="min-h-screen pb-16">
            {/* Header */}
            <header className="border-b-2 border-black bg-ink px-4 lg:px-12 py-4 flex flex-wrap items-center justify-between gap-3 sticky top-0 z-30">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-volt border-2 border-black flex items-center justify-center">
                        <Shield className="w-4 h-4 text-white" strokeWidth={2.5} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Site Admin</p>
                        <h1 className="font-display font-black text-lg lg:text-xl leading-none">
                            {admin.email} {admin.is_super && <span className="text-cyber text-xs">SUPER</span>}
                        </h1>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/" className="brutal-btn-ghost text-xs py-2" data-testid="site-admin-home-link">
                        <ArrowLeft className="w-3 h-3" /> Home
                    </Link>
                    <button onClick={handleLogout} className="brutal-btn-ghost text-xs py-2" data-testid="site-admin-logout-btn">
                        <LogOut className="w-3 h-3" /> Sign out
                    </button>
                </div>
            </header>

            <div className="px-4 lg:px-12 py-6 max-w-7xl mx-auto space-y-6">
                {/* iter80 — Platform Admin reminder banner. Platform identities
                    have their own /platform route; this banner just reminds
                    platform admins where the Platform Console lives. */}
                {admin?.role === "platform_admin" && (
                    <div
                        data-testid="platform-admin-reminder-banner"
                        className="brutal-card-light p-3 flex items-center gap-3 border-volt/40 bg-volt/5"
                    >
                        <Shield className="w-5 h-5 text-volt shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-widest text-volt font-bold">Heads up — you are a Commons Admin</p>
                            <p className="text-xs text-zinc-300 mt-1">
                                You're viewing the <span className="font-bold">tenant Site Admin</span> surface for TCR. Commons-level controls (Release Ops, Tenants, Release Notes, Docs, Commons Roles) live in the Commons Console.
                            </p>
                        </div>
                        <Link
                            to="/platform"
                            data-testid="goto-platform-console-link"
                            className="brutal-btn bg-volt text-black text-xs py-2 px-3 whitespace-nowrap"
                        >
                            <Landmark className="w-3 h-3" /> Commons Console
                        </Link>
                    </div>
                )}

                {/* Nav — iter86.36: Commons-Console-style top-level nav
                    (standalone tab + grouped dropdowns, per user request).
                    Overview | Rivalry Ops ▾ | Admin Ops ▾ | Console ▾
                    Tab keys + testids stay stable; presentation only. */}
                {(() => {
                    const NAV = [
                        { type: "tab", k: "overview", label: "Overview", icon: Activity },
                        { type: "group", g: "rivalry_ops", label: "Rivalry Ops", icon: Trophy, items: [
                            { k: "leagues", label: "Rivalry Manager", icon: Trophy },
                            { k: "subscriptions", label: "Rivalry Subscriptions", icon: CreditCard },
                            { k: "custom_subs", label: "Custom Subscriptions", icon: Crown },
                            { k: "pro_plus", label: "Pro+ Subscriptions", icon: Star },
                            { k: "tools_audit", label: "Tools & Audit", icon: AlertTriangle },
                        ]},
                        { type: "group", g: "admin_ops", label: "Admin Ops", icon: ShieldCheck, items: [
                            { k: "tournaments", label: "Templates", icon: FileJson },
                            { k: "permissions", label: "Admin Permissions", icon: ShieldCheck },
                            { k: "compliance", label: "Compliance", icon: ShieldCheck },
                            { k: "llm_monitor", label: "Comms Monitor", icon: Sparkles },
                        ]},
                        { type: "group", g: "console", label: "Console", icon: Key, items: [
                            { k: "branding", label: "Branding", icon: Palette },
                            { k: "admins", label: "Site Admins", icon: Shield },
                            { k: "account", label: "Account", icon: Key },
                        ]},
                    ];
                    const activeLabel = (k) => {
                        for (const n of NAV) {
                            if (n.type === "group") {
                                const hit = n.items.find((i) => i.k === k);
                                if (hit) return hit.label;
                            }
                        }
                        return null;
                    };
                    return (
                        <div className="flex flex-wrap items-center gap-2 border-b-2 border-[#333] pb-3">
                            {NAV.map((n) => {
                                const Icon = n.icon;
                                if (n.type === "tab") {
                                    const isActive = tab === n.k;
                                    return (
                                        <button key={n.k}
                                            data-testid={`site-admin-group-${n.k}-btn`}
                                            onClick={() => setTab(n.k)}
                                            className={`brutal-btn-ghost text-xs py-2 px-3 ${isActive ? "border-volt text-volt" : ""}`}>
                                            <Icon className="w-3 h-3" /> {n.label}
                                        </button>
                                    );
                                }
                                const groupActive = n.items.some((i) => i.k === tab);
                                return (
                                    <DropdownMenu key={n.g}>
                                        <DropdownMenuTrigger asChild>
                                            <button
                                                data-testid={`site-admin-group-${n.g}-btn`}
                                                className={`brutal-btn-ghost text-xs py-2 px-3 ${groupActive ? "border-volt text-volt" : ""}`}>
                                                <Icon className="w-3 h-3" /> {n.label}
                                                {groupActive && (
                                                    <span className="text-[9px] font-bold uppercase text-zinc-400">· {activeLabel(tab)}</span>
                                                )}
                                                <ChevronDown className="w-3 h-3 opacity-60" />
                                            </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="bg-[#0f0f0f] border-2 border-[#333] text-white min-w-[190px]">
                                            {n.items.map((i) => {
                                                const ItemIcon = i.icon;
                                                const itemActive = tab === i.k;
                                                return (
                                                    <DropdownMenuItem key={i.k}
                                                        onClick={() => setTab(i.k)}
                                                        data-testid={`site-admin-tab-${i.k}-btn`}
                                                        className={`text-xs gap-2 cursor-pointer focus:bg-volt/10 focus:text-volt ${itemActive ? "text-volt font-bold" : "text-zinc-300"}`}>
                                                        <ItemIcon className="w-3 h-3" /> {i.label}
                                                    </DropdownMenuItem>
                                                );
                                            })}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                );
                            })}
                            <button
                                data-testid="site-admin-refresh-view-btn"
                                onClick={refreshView}
                                disabled={refreshing}
                                title="Refresh the current view — re-pulls this tab's data without reloading the whole page"
                                className="brutal-btn-ghost text-xs py-2 px-3 ml-auto text-zinc-400 hover:text-volt disabled:opacity-50"
                            >
                                <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                            </button>
                        </div>
                    );
                })()}

                {/* Bulk action bar — admins tab */}
                {tab === "admins" && selectedAdminIds.size > 0 && (
                    <div className="brutal-card p-3 flex items-center justify-between flex-wrap gap-3 sticky top-[88px] z-20 bg-cyber/10 border-cyber" data-testid="admins-bulk-action-bar">
                        <p className="text-sm font-bold">
                            <span className="text-cyber">{selectedAdminIds.size}</span> site admin{selectedAdminIds.size === 1 ? "" : "s"} selected
                        </p>
                        <div className="flex gap-2">
                            <button
                                data-testid="clear-admins-selection-btn"
                                onClick={clearSelectedAdmins}
                                className="brutal-btn-ghost text-xs py-2"
                            >
                                <X className="w-3.5 h-3.5" /> Clear
                            </button>
                            <button
                                data-testid="bulk-delete-admins-btn"
                                onClick={bulkDeleteAdmins}
                                disabled={bulkDeleting}
                                className="brutal-btn text-xs py-2 bg-heat text-white border-black shadow-brutal disabled:opacity-60"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> {bulkDeleting ? "Deleting..." : `Delete ${selectedAdminIds.size} selected`}
                            </button>
                        </div>
                    </div>
                )}

                {/* iter86.72 — keyed wrapper: refresh button remounts the
                    active tab body so it refetches without a page reload. */}
                <div key={viewKey} className="space-y-6" data-testid="site-admin-view-container">

                {tab === "mobile_access" && <MobileAccessTab />}
                {tab === "subscriptions" && (
                    <div className="space-y-4">
                        {/* iter87.85 — Reshape into labeled collapsible sections.
                            Comp Subscriptions (top) + Incoming Requests (bottom).
                            Public gate + landing controls stay INSIDE
                            SiteAdminLeagueRequests since that component owns them. */}
                        <CollapsibleCard
                            icon={Star}
                            title="Comp Subscriptions"
                            subtitle="Rivalries granted comp / trial tier with time-boxed access"
                            testId="subs-comp-card"
                            defaultOpen={true}
                        >
                            <SiteAdminSubscriptions />
                        </CollapsibleCard>
                        <CollapsibleCard
                            icon={Inbox}
                            title="Comp / Trial Approvals"
                            subtitle="Pending queue · approved / rejected history for comp-code rivalries"
                            testId="subs-requests-card"
                            defaultOpen={true}
                        >
                            <SiteAdminLeagueRequests />
                        </CollapsibleCard>
                    </div>
                )}
                {tab === "tools_audit" && <ToolsAndAuditTab />}
                {tab === "pro_plus" && <ProPlusSubscriptionsTab />}
                {tab === "custom_subs" && <CustomSubscriptionsTab />}

                {/* iter79 — Release Ops tab (Slice A.1 shell) */}
                {tab === "release_ops" && (
                    <ReleaseOpsTab viewerRole={admin?.role} />
                )}

                {/* Overview */}
                {tab === "overview" && stats && (
                    <OverviewTab admin={admin} scopeView={scopeView} stats={stats} onCleanup={loadAll} />
                )}

                {/* Rivalries */}
                {tab === "leagues" && (
                    <LeaguesTab admin={admin} leagues={leagues} loadAll={loadAll} />
                )}

                {tab === "tournaments" && (
                    <div className="space-y-4">
                        {/* iter87.229 (user-reported) — Template Availability's
                            toggles looked broken because, for most sport shapes,
                            enable/disable is fully decided by the Commons
                            Console's Template Visibility (platform_admin-only).
                            Card below is now read-only for everyone; banner
                            always shows so every site admin understands why. */}
                        <div
                            className="border border-emerald-800 bg-emerald-950/20 rounded p-3 text-xs text-emerald-200"
                            data-testid="templates-moved-pointer"
                        >
                            Which templates rivalry admins can pick is controlled in{" "}
                            {(admin?.role === "platform_admin" || admin?.is_super) ? (
                                <a href="/platform" className="underline font-bold">Commons Console → Product Ops → Templates</a>
                            ) : (
                                <strong>Commons Console → Product Ops → Templates</strong>
                            )}
                            {" "}— the availability list below is read-only. Ask your platform admin for changes.
                        </div>
                        <SiteAdminTemplateAvailability />
                        <CustomFeaturesCard />
                        <TemplateScheduleInspectorCard />
                    </div>
                )}

                {/* Rivalry Admin Permissions (iter35a — co-admin role) */}
                {tab === "permissions" && (
                    <SiteAdminCoAdminPermissions />
                )}

                {/* iter87.81 — old standalone Requests + Subscriptions blocks
                    removed. Both now render together under the consolidated
                    Rivalry Subscriptions tab (see block near top of switch). */}

                {/* iter63z''''''' — Whitelabel branding (Phase 2 v1) */}
                {tab === "branding" && (
                    <TenantBrandingTab />
                )}

                {/* iter67 — Cross-tenant management, gated to platform_admin. */}
                {tab === "tenants" && admin?.role === "platform_admin" && (
                    <PlatformTenantsTab />
                )}

                {tab === "releases" && admin?.role === "platform_admin" && scopeView === "platform" && (
                    <ReleaseNotesTab />
                )}

                {tab === "docs" && admin?.role === "platform_admin" && scopeView === "platform" && (
                    <DocsTab />
                )}

                {tab === "compliance" && (
                    <ComplianceTab scope="site" />
                )}

                {tab === "llm_monitor" && (
                    <AdminOpsTab />
                )}

                {tab === "platform_admins" && admin?.role === "platform_admin" && scopeView === "platform" && (
                    <PlatformAdminsPlaceholderTab />
                )}

                {/* Site Admins */}
                {tab === "admins" && (
                    <AdminsTab
                        admin={admin}
                        siteAdmins={siteAdmins}
                        eligibleAdminIds={eligibleAdminIds}
                        selectedAdminIds={selectedAdminIds}
                        adminSelectable={adminSelectable}
                        onToggleAll={toggleAllAdmins}
                        onToggleAdmin={toggleAdminSelected}
                        onOpenInvite={() => setInviteOpen(true)}
                        onAdminDeleted={(list) => setSiteAdmins(list)}
                    />
                )}

                {/* Account */}
                {tab === "account" && (
                    <div className="space-y-8">
                        <AccountTab admin={admin} onUpdated={(updated) => setAdmin({ ...admin, ...updated })} onOpenChangePassword={() => setChangePassOpen(true)} />
                        {admin?.role === "platform_admin" && scopeView === "platform" && (
                            <div className="pt-6 border-t-2 border-[#222]">
                                <PlatformPasscodeManager />
                            </div>
                        )}
                    </div>
                )}
                </div>
            </div>

            {inviteOpen && <InviteModal
                onClose={() => setInviteOpen(false)}
                onCreated={() => api.authListSiteAdmins().then(setSiteAdmins)}
                inviterRole={admin?.role}
                inviterTenantId={admin?.tenant_id}
            />}
            {changePassOpen && <ChangePasswordModal onClose={() => setChangePassOpen(false)} />}
        </div>
    );
}
